import { AgGrid } from "@jsq/ds/core";
import { type OverviewFilters } from "~/graphql/supergraphTypes";
import { DATE_FORMATS } from "~/utils/constants";
import { formatFullDateAndTimeWithLocalTimezone } from "~/utils/datetime";

/**
 * Filter information interface for AgGrid filter models
 */
type FilterInfo = {
  filterType: "text" | "set" | "date";
  filter?: string;
  values?: string[];
  dateFrom?: string;
  type?: string;
};

/**
 * Transforms AgGrid filter model into billing filter variables for GraphQL queries
 * Supports text, set, and date filter types (used by EntitiesExport)
 * @param filterModel AgGrid filter model or null
 * @returns Array of OverviewFilters or null if no valid filters
 */
export const getBillingFilterVars = (
  filterModel: AgGrid.FilterModel | null,
): OverviewFilters[] | null => {
  if (!filterModel) {
    return null;
  }

  const queryFilters: OverviewFilters[] = [];

  Object.entries(filterModel).forEach(([field, filterInfo]: [string, FilterInfo]) => {
    // Handle text filter type
    if (filterInfo.filterType === "text" && filterInfo.filter) {
      queryFilters.push({ field: field, filterValues: [filterInfo.filter] });
    }
    // Handle set filter type
    else if (filterInfo.filterType === "set" && filterInfo.values !== undefined) {
      // Include empty arrays for set filters
      queryFilters.push({ field: field, filterValues: filterInfo.values });
    }
    // Handle date filter type
    else if (filterInfo.filterType === "date" && filterInfo.dateFrom) {
      if (filterInfo.type === "equals" || !filterInfo.type) {
        queryFilters.push({
          field: field,
          filterValues: [filterInfo.dateFrom],
        });
      } else {
        throw Error("Only equals filter type is supported for date filters");
      }
    }
  });

  return queryFilters.length > 0 ? queryFilters : null;
};

/**
 * Formats a date string for display in the Billing section
 * returns a fallback string if formatting fails
 * @param dateString The date string to format
 * @returns Formatted date string or "-" if input is null/undefined
 */
export const formatDate = (dateString: string | null): string => {
  if (!dateString) return "-";

  try {
    return formatFullDateAndTimeWithLocalTimezone(dateString, DATE_FORMATS["MM/DD/YYYY"].long);
  } catch {
    return dateString;
  }
};

/**
 * Capitalizes the first letter of a string
 * @param str The string to capitalize
 * @returns The capitalized string or an empty string if the input is null/undefined
 */
export const capitalizeString = (str: string | undefined | null): string => {
  if (!str) {
    return "";
  }
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const displayValueOrDash = (value: string | null): string => {
  if (!value) return "-";
  return value;
};
